import { z } from 'zod';
import { BillingDto } from './billing.js';
import { CHANNEL_ROUTES } from './channels.js';
import { CHAT_ATTACHMENT_MAX_BYTES, CHAT_ATTACHMENT_TYPES, CHAT_ATTACHMENTS_PER_MESSAGE } from './chat.js';
import { FlowBinding } from './flow.js';
import { AgentRuntimeId, Guardrails, ModelParams, ToolPolicyRule } from './guardrails.js';
import { KB_ROUTES } from './kb.js';
import { KNOWLEDGE_ROUTES } from './knowledge.js';
import { McpBinding, WORKSPACE_ACTIONS } from './mcp.js';
import { Announcement } from './platform-admin.js';
import { ModelPrice, ModelRef, ProviderType } from './providers.js';
import type { Permission } from './rbac.js';
import { ROLES } from './rbac.js';
import { PlanInput, PlanReviewOutcome, RunPhase, RunSettings, RunSettingsOverride } from './run-settings.js';
import { SkillBinding } from './skill.js';

/**
 * HTTP API contract shared by `apps/api` (validation) and `apps/web` (typed client).
 * All routes live under `/api`, are JSON-only, org-scoped by the session's active organization
 * and require the permission noted per route (see rbac.ts).
 */

export const Id = z.string().min(1).max(64);
/** Current provider metadata for a chat artifact; null means the connection cannot resolve it. */
export const ArtifactPreview = z.object({
  kind: z.enum(['pull_request', 'trello_card', 'trello_board']),
  title: z.string(),
  status: z.string().nullable(),
  description: z.string().nullable(),
  context: z.string().nullable(),
});
export type ArtifactPreview = z.infer<typeof ArtifactPreview>;
export const ArtifactPreviewResult = z.object({ preview: ArtifactPreview.nullable() });
export type ArtifactPreviewResult = z.infer<typeof ArtifactPreviewResult>;
export const Slug = z
  .string()
  .min(2)
  .max(40)
  .regex(/^[a-z0-9][a-z0-9-]*$/);

export const Paginated = <T extends z.ZodType>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullable() });

export const ApiError = z.object({ error: z.object({ code: z.string(), message: z.string() }) });
export type ApiError = z.infer<typeof ApiError>;

// ---------- Providers & models ----------
/**
 * Anthropic providers: how the Claude Code runtime authenticates. `subscription`/`hybrid` (a pasted
 * setup-token) need Claude mode `pasted_token` (self-hosted only); `sandbox_login`/`hybrid_login` (each
 * person's own Claude Code login inside their agents' sandbox, docs/claude-login-sandbox.md) need mode
 * `sandbox_login`. Other runtimes and background work use the API key.
 */
export const ProviderAuthMode = z.enum([
  'api_key',
  'subscription',
  'hybrid',
  'sandbox_login',
  'hybrid_login',
]);
export type ProviderAuthMode = z.infer<typeof ProviderAuthMode>;
/** Claude subscription token from `claude setup-token` (starts with `sk-ant-oat`). Write-only. */
export const SubscriptionToken = z
  .string()
  .trim()
  .min(20)
  .max(500)
  .regex(/^sk-ant-oat/, 'Expected a token from `claude setup-token` (sk-ant-oat…), not an API key');

export const ProviderInput = z.object({
  slug: Slug,
  type: ProviderType,
  name: z.string().min(1).max(80),
  baseUrl: z.string().url().max(500).optional(),
  /** Write-only. Never returned by the API. */
  apiKey: z.string().min(1).max(500).optional(),
  enabled: z.boolean().default(true),
  authMode: ProviderAuthMode.default('api_key'),
  subscriptionToken: SubscriptionToken.optional(),
});
export type ProviderInput = z.infer<typeof ProviderInput>;

export const ProviderDto = z.object({
  id: Id,
  slug: z.string(),
  type: ProviderType,
  name: z.string(),
  baseUrl: z.string().nullable(),
  enabled: z.boolean(),
  /** e.g. "••••a1b2", null when no key stored. */
  apiKeyHint: z.string().nullable(),
  authMode: ProviderAuthMode,
  /** Hint of the stored Claude subscription token, null when none. */
  subscriptionTokenHint: z.string().nullable(),
  /** Anthropic + server switch on: subscription/hybrid can be selected. */
  subscriptionAvailable: z.boolean(),
  /** Anthropic + Claude mode `sandbox_login`: sandbox_login/hybrid_login can be selected. */
  claudeLoginAvailable: z.boolean().optional(),
  /** Outcome of the last connection test; null when it was never tested. */
  lastTest: z
    .object({
      at: z.string(),
      ok: z.boolean(),
      error: z.string().nullable(),
      latencyMs: z.number().nullable(),
      modelCount: z.number().nullable(),
    })
    .nullable()
    .optional(),
  createdAt: z.string(),
});
export type ProviderDto = z.infer<typeof ProviderDto>;

/** The signed-in person's own Claude Code login for a provider (never a credential: hints only). */
export const ClaudeLoginDto = z.object({
  id: Id,
  status: z.enum(['pending', 'awaiting_code', 'connected', 'expired', 'revoked', 'error']),
  /** Anthropic's sign-in page, shown while the CLI waits for the code. */
  loginUrl: z.string().nullable(),
  accountEmailHint: z.string().nullable(),
  planHint: z.string().nullable(),
  connectedAt: z.string().nullable(),
  lastOkAt: z.string().nullable(),
  lastErrorCode: z.string().nullable(),
});
export type ClaudeLoginDto = z.infer<typeof ClaudeLoginDto>;
export const AgentLoginOwnerInput = z.object({ mine: z.boolean() });
/** The code Anthropic's page shows after sign-in; handed to the CLI's prompt once, never stored. */
export const ClaudeLoginCodeInput = z.object({ code: z.string().trim().min(4).max(2000) });

export const CodexLoginResult = z.object({
  loginId: z.string().uuid(),
  status: z.enum(['pending', 'connected', 'failed']),
  verificationUrl: z.string().url().optional(),
  userCode: z.string().optional(),
  email: z.string().optional(),
  planType: z.string().optional(),
  error: z.string().optional(),
});
export type CodexLoginResult = z.infer<typeof CodexLoginResult>;

export const ModelDto = z.object({
  id: Id,
  providerId: Id,
  modelId: z.string(),
  displayName: z.string(),
  contextWindow: z.number().nullable(),
  supportsTools: z.boolean(),
  supportsVision: z.boolean(),
  supportsReasoning: z.boolean(),
  enabled: z.boolean(),
  price: ModelPrice.nullable(),
  priceSource: z.enum(['models.dev', 'litellm', 'manual']).nullable(),
});
export type ModelDto = z.infer<typeof ModelDto>;

export const PriceOverrideInput = ModelPrice;

// ---------- Agents ----------
export const AgentVersionInput = z.object({
  instructions: z.string().max(50_000),
  modelRef: ModelRef,
  /** `claude-agent-sdk` requires `modelRef` to point at an `anthropic` provider (validated by the API). */
  runtime: AgentRuntimeId.default('ai-sdk'),
  params: ModelParams.default({}),
  guardrails: Guardrails.default(Guardrails.parse({})),
  toolPolicy: z.array(ToolPolicyRule).max(200).default([]),
  /** Built-in platform tools enabled for this agent. */
  builtinTools: z.array(z.string()).max(50).default([]),
  /** MCP servers bound to this version (tools appear as `mcp.<prefix>.<tool>`). Omitted = none. */
  mcpBindings: z.array(McpBinding).max(20).optional(),
  /** Flows (recording mode) this version may run. Omitted = none. */
  flowBindings: z.array(FlowBinding).max(20).optional(),
  /** Skills this version may load (`load_skill`). Omitted = none. */
  skillBindings: z.array(SkillBinding).max(50).optional(),
  changelog: z.string().max(500).optional(),
});
export type AgentVersionInput = z.infer<typeof AgentVersionInput>;

/** Read-only evaluation of a saved version or an unsaved permission draft. */
export const PermissionPreviewInput = AgentVersionInput.pick({
  guardrails: true,
  toolPolicy: true,
  builtinTools: true,
  mcpBindings: true,
  flowBindings: true,
  skillBindings: true,
});
export type PermissionPreviewInput = z.infer<typeof PermissionPreviewInput>;

/** Explicit targets and version expectations prevent silently overwriting concurrent edits. */
export const BulkAgentPermissionsInput = z.object({
  targets: z
    .array(z.object({ id: Id, versionId: Id }))
    .min(1)
    .max(100)
    .refine((targets) => new Set(targets.map((t) => t.id)).size === targets.length, 'Duplicate agents'),
  changes: z
    .object({
      executionApproval: Guardrails.shape.executionApproval.removeDefault().optional(),
      toolPolicy: AgentVersionInput.shape.toolPolicy.removeDefault().optional(),
      builtinTools: AgentVersionInput.shape.builtinTools.removeDefault().optional(),
      egressAllowlist: Guardrails.shape.egressAllowlist.removeDefault().optional(),
      pushBranches: Guardrails.shape.git.unwrap().shape.pushBranches.removeDefault().optional(),
    })
    .strict()
    .refine((changes) => Object.keys(changes).length > 0, 'Select at least one permission setting'),
});
export type BulkAgentPermissionsInput = z.infer<typeof BulkAgentPermissionsInput>;
export const PermissionPreview = z.object({
  tools: z.array(
    z.object({
      name: z.string(),
      effect: z.string(),
      source: z.string(),
      standard: z.enum(['allow', 'ask', 'deny', 'conditional']),
      auto: z.enum(['allow', 'ask', 'deny', 'conditional']),
      rule: ToolPolicyRule.nullable(),
      reason: z.enum(['mandatory', 'input', 'rule', 'default', 'human_question', 'integration']),
    }),
  ),
  integrations: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      status: z.enum(['cached', 'unknown', 'disabled', 'needs_auth']),
      readOnly: z.boolean(),
    }),
  ),
});
export type PermissionPreview = z.infer<typeof PermissionPreview>;

export const AgentInput = z.object({
  name: z.string().min(1).max(80),
  slug: Slug,
  roleTitle: z.string().max(80).default(''),
  description: z.string().max(1000).default(''),
  avatar: z.string().max(16).default('⏺'),
  managerAgentId: Id.nullable().default(null),
  version: AgentVersionInput,
});
export type AgentInput = z.infer<typeof AgentInput>;

export const AgentPatch = z.object({
  name: z.string().min(1).max(80).optional(),
  roleTitle: z.string().max(80).optional(),
  description: z.string().max(1000).optional(),
  avatar: z.string().max(16).optional(),
  managerAgentId: Id.nullable().optional(),
  status: z.enum(['active', 'paused', 'archived']).optional(),
});

export const AgentVersionDto = AgentVersionInput.extend({
  id: Id,
  version: z.number().int(),
  createdAt: z.string(),
  createdBy: z.string().nullable(),
});
export type AgentVersionDto = z.infer<typeof AgentVersionDto>;

export const AgentDto = z.object({
  id: Id,
  name: z.string(),
  slug: z.string(),
  roleTitle: z.string(),
  description: z.string(),
  avatar: z.string(),
  status: z.enum(['active', 'paused', 'archived']),
  kind: z.enum(['persistent', 'ephemeral']),
  managerAgentId: Id.nullable(),
  currentVersion: AgentVersionDto.nullable(),
  stats: z
    .object({
      runsToday: z.number(),
      costTodayUsd: z.number(),
      costMonthUsd: z.number(),
      activeRuns: z.number(),
    })
    .optional(),
  createdAt: z.string(),
});
export type AgentDto = z.infer<typeof AgentDto>;

/** A git working copy in the agent's sandbox (docs/git-workspaces.md), as last seen by the platform. */
export const AgentWorkspaceDto = z.object({
  id: Id,
  path: z.string(),
  host: z.string(),
  repo: z.string(),
  branch: z.string().nullable(),
  base: z.string().nullable(),
  /** Commits not on the remote yet. */
  ahead: z.number().int(),
  dirty: z.boolean(),
  /** Extra copy made because the main one was busy (removed automatically once published/unused). */
  extra: z.boolean(),
  prUrl: z.string().nullable(),
  diskMb: z.number().nullable(),
  lastUsedAt: z.string(),
});
export type AgentWorkspaceDto = z.infer<typeof AgentWorkspaceDto>;
export const AgentWorkspacesResult = z.object({
  items: z.array(AgentWorkspaceDto),
  /** Memory the platform raised the sandbox to after out-of-memory kills (null = runner default). */
  sandboxMemoryMb: z.number().nullable(),
  /** Admin maximum (guardrails.sandboxLimits.memoryMb; null = the runner ceiling). */
  maxMemoryMb: z.number().nullable(),
});
export type AgentWorkspacesResult = z.infer<typeof AgentWorkspacesResult>;
/**
 * Delegation quality over the last `days` (deterministic, from tasks + runs): did the delegator pick the
 * right colleague? `doneFirstTry`: done in one run and not handed to someone else; `redelegated`: the
 * delegator gave the same parent task to a different assignee afterwards; `failed`: failed or cancelled.
 */
export const DelegationStat = z.object({
  delegated: z.number().int(),
  doneFirstTry: z.number().int(),
  redelegated: z.number().int(),
  failed: z.number().int(),
});
export type DelegationStat = z.infer<typeof DelegationStat>;
export const AgentDelegationStatsResult = z.object({
  days: z.number().int(),
  /** Work this agent delegated, in total and per assignee. */
  asDelegator: DelegationStat,
  assignees: z.array(DelegationStat.extend({ agentId: Id, name: z.string() })),
  /** Work delegated to this agent, by anyone. */
  asAssignee: DelegationStat,
});
export type AgentDelegationStatsResult = z.infer<typeof AgentDelegationStatsResult>;
export const WorkspaceActionInput = z.object({
  action: z.enum(WORKSPACE_ACTIONS),
  /** For `remove`: the working copy path. */
  path: z.string().min(1).max(512).optional(),
});
export type WorkspaceActionInput = z.infer<typeof WorkspaceActionInput>;

// ---------- Conversations / chat ----------
export const ConversationDto = z.object({
  id: Id,
  agentId: Id,
  title: z.string(),
  /** `master` = company-wide master chat (see knowledge.ts). */
  kind: z.enum(['agent', 'master']).optional(),
  modelRef: z.string().nullable().optional(),
  /** Agent replies its owner has not read yet (at least 1 when marked unread by hand). */
  unread: z.number().int().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ConversationDto = z.infer<typeof ConversationDto>;

export const MessageDto = z.object({
  id: Id,
  role: z.enum(['user', 'assistant', 'system']),
  /** AI SDK UIMessage parts (text, reasoning, tool-*, …). */
  parts: z.array(z.record(z.string(), z.unknown())),
  runId: Id.nullable(),
  createdAt: z.string(),
  /** Only for a message typed while a run was going: waiting in the run's inbox → shown to the agent → taken into a turn. */
  inboxStatus: z.enum(['queued', 'delivered', 'processed']).optional(),
});
export type MessageDto = z.infer<typeof MessageDto>;

/** Screenshots uploaded before sending (`uploadAttachment`), sent along with the message. */
export const AttachmentIds = z.array(Id).max(CHAT_ATTACHMENTS_PER_MESSAGE);
export const SendMessageInput = z
  .object({
    text: z.string().max(100_000).default(''),
    attachmentIds: AttachmentIds.optional(),
    runSettings: RunSettingsOverride.optional(),
  })
  .refine((b) => b.text.trim().length > 0 || (b.attachmentIds?.length ?? 0) > 0, {
    message: 'text or an attachment is required',
    path: ['text'],
  });
export const UploadAttachmentInput = z.object({
  name: z.string().min(1).max(200),
  mediaType: z.enum(CHAT_ATTACHMENT_TYPES),
  /** Base64 of the image bytes. */
  data: z
    .string()
    .min(1)
    .max(Math.ceil((CHAT_ATTACHMENT_MAX_BYTES * 4) / 3) + 4),
});
export const AttachmentDto = z.object({
  id: Id,
  name: z.string(),
  mediaType: z.string(),
  sizeBytes: z.number(),
});
export type AttachmentDto = z.infer<typeof AttachmentDto>;
export const SendMessageResult = z.object({ messageId: Id, runId: Id });

// ---------- Runs ----------
export const RunDto = z.object({
  autoConsent: z
    .object({ enabledAt: z.string(), expiresAt: z.string(), active: z.boolean() })
    .nullable()
    .optional(),
  runSettings: RunSettings.nullable().optional(),
  phase: RunPhase.optional(),
  approvedPlan: PlanInput.nullable().optional(),
  id: Id,
  agentId: Id,
  agentVersionId: Id,
  taskId: Id.nullable(),
  conversationId: Id.nullable(),
  parentRunId: Id.nullable(),
  /** First run of the delegation tree (null for a top-level run). */
  rootRunId: Id.nullable().optional(),
  mode: z.enum(['chat', 'task', 'subagent']),
  status: z.string(),
  depth: z.number().int(),
  stepCount: z.number().int(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  cacheReadTokens: z.number(),
  cacheWriteTokens: z.number(),
  reasoningTokens: z.number(),
  costUsd: z.number(),
  error: z.string().nullable(),
  startedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  createdAt: z.string(),
  /** The unanswered `ask_human` question while the run is `awaiting_human`. */
  question: z
    .object({ text: z.string(), options: z.array(z.string()) })
    .nullable()
    .optional(),
  /** While `scheduled`: why the run waits, on which provider, and when it resumes automatically. */
  scheduled: z
    .object({
      reason: z.string(),
      provider: z.string().nullable(),
      resumeAt: z.string(),
      /** Automatic resumptions used so far (the cap is a worker setting). */
      retryCount: z.number().int(),
    })
    .nullable()
    .optional(),
  /** "Continue" is possible: the run is scheduled, or failed on a usage limit with its progress kept. */
  canContinue: z.boolean().optional(),
  /** "Restart" (a new run with the same input) is possible for this finished run. */
  canRestart: z.boolean().optional(),
  /** The run this one was restarted from. */
  restartedFromRunId: Id.nullable().optional(),
});
export type RunDto = z.infer<typeof RunDto>;

export const RunContextDto = z.object({
  memories: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      type: z.string(),
      scope: z.string(),
      trust: z.string(),
      how: z.string(),
      cited: z.boolean(),
    }),
  ),
  skills: z.array(
    z.object({
      skillId: z.string(),
      name: z.string(),
      version: z.number(),
      how: z.enum(['catalog', 'loaded']),
    }),
  ),
  chain: z.array(
    z.object({
      runId: z.string(),
      ref: z.string(),
      agentName: z.string(),
      title: z.string().nullable(),
      summary: z.string(),
      untrusted: z.boolean(),
    }),
  ),
});
export type RunContextDto = z.infer<typeof RunContextDto>;

// ---------- Tasks ----------
export const TaskInput = z.object({
  runSettings: RunSettingsOverride.optional(),
  title: z.string().min(1).max(200),
  input: z.string().max(50_000).default(''),
  assigneeAgentId: Id,
  priority: z.number().int().min(1).max(10).default(5),
  dependsOn: z.array(Id).max(50).default([]),
  dueAt: z.string().datetime().optional(),
});
export type TaskInput = z.infer<typeof TaskInput>;

// ---------- Approvals ----------
export const ApprovalDto = z.object({
  builtinAccessResult: z
    .object({
      approvalId: Id,
      targets: z.array(z.object({ agentId: Id, versionId: Id, changed: z.boolean() })),
    })
    .nullable()
    .optional(),
  kind: z.enum(['tool', 'plan']).optional(),
  plan: PlanInput.nullable().optional(),
  planVersion: z.number().int().nullable().optional(),
  review: PlanReviewOutcome.nullable().optional(),
  reviewerAgentId: Id.nullable().optional(),
  reviewerAgentName: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
  id: Id,
  runId: Id,
  agentId: Id,
  /** Display name of the requesting agent (resolved server-side; also for the hidden company-chat agent). */
  agentName: z.string().optional(),
  /** First run of the delegation tree the requesting run belongs to (null when it is a top-level run). */
  rootRunId: Id.nullable().optional(),
  /** Status of the requesting run: a pending approval of a run that no longer waits for it is outdated. */
  runStatus: z.string().optional(),
  toolName: z.string(),
  inputPreview: z.string(),
  status: z.enum(['pending', 'approved', 'denied', 'expired']),
  decidedBy: z.string().nullable(),
  decidedVia: z.enum(['web', 'telegram', 'system']).nullable(),
  /** Display name of the deciding user (resolved server-side). */
  decidedByName: z.string().nullable().optional(),
  /** Set when the decision also created/updated a persistent tool policy rule. */
  rememberedPolicyId: z.string().nullable().optional(),
  expiresAt: z.string(),
  createdAt: z.string(),
});
export type ApprovalDto = z.infer<typeof ApprovalDto>;

/**
 * An `ask_human` question of a run (`listQuestions`). Open ones come from `runs.pending` (status
 * `awaiting_human`, no answer yet); answered ones from the `run.answer` audit entry.
 */
export const QuestionDto = z.object({
  runId: Id,
  /** First run of the delegation tree the asking run belongs to (null when it is a top-level run). */
  rootRunId: Id.nullable(),
  agentId: Id,
  agentName: z.string(),
  conversationId: Id.nullable(),
  taskId: Id.nullable(),
  state: z.enum(['open', 'answered']),
  question: z.string(),
  options: z.array(z.string()),
  /** When the run paused for the question. */
  askedAt: z.string(),
  /** Only for answered questions: (a preview of) the answer, who gave it and when. */
  answer: z.string().nullable(),
  answeredBy: z.string().nullable(),
  answeredByName: z.string().nullable(),
  answeredAt: z.string().nullable(),
});
export type QuestionDto = z.infer<typeof QuestionDto>;
export const QuestionsQuery = z.object({
  state: z.enum(['open', 'answered']).optional(),
});

/**
 * A host the egress proxy refused an agent's sandbox (`listEgressRequests`). Pending ones wait in the
 * Approvals inbox; approving adds the host to the agent's egress allowlist, denying tells the agent.
 */
export const EgressRequestDto = z.object({
  id: Id,
  runId: Id,
  /** First run of the delegation tree the blocked run belongs to (null when it is a top-level run). */
  rootRunId: Id.nullable(),
  /** Whether the run is still going (an approval applies to its next sandbox command). */
  runStatus: z.string().nullable(),
  /** The agent that owns the sandbox. */
  agentId: Id,
  agentName: z.string(),
  host: z.string(),
  status: z.enum(['pending', 'approved', 'denied']),
  reason: z.string().nullable(),
  decidedBy: z.string().nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type EgressRequestDto = z.infer<typeof EgressRequestDto>;
export const EgressRequestsQuery = z.object({
  state: z.enum(['pending', 'decided']).optional(),
});
export const EgressRequestDecision = z.object({
  approved: z.boolean(),
  reason: z.string().max(500).optional(),
});
export type EgressRequestDecision = z.infer<typeof EgressRequestDecision>;

export const ApprovalDecision = z.object({
  planDecision: z.enum(['approve', 'request_changes', 'reject']).optional(),
  approved: z.boolean(),
  reason: z.string().max(500).optional(),
  /**
   * `always`: also store an agent-scoped tool policy rule (exact tool name, allow/deny) so future
   * calls are decided without asking. Requires `policy:update` in addition to `approval:decide`.
   */
  remember: z.enum(['once', 'always']).default('once'),
});
export type ApprovalDecisionInput = z.input<typeof ApprovalDecision>;

// ---------- Usage ----------
export const UsageQuery = z.object({
  from: z.string().date(),
  to: z.string().date(),
  agentId: Id.optional(),
  /**
   * `kind` = run | reflection | consolidation | embedding | flow_synthesis | skill_draft | run_analysis (what the AI call was for);
   * `billing` = api | subscription (API key vs Claude subscription, $0 but tokens count);
   * `purpose` = `<kind>:<billing>` (e.g. `reflection:subscription`).
   */
  groupBy: z.enum(['day', 'agent', 'model', 'provider', 'kind', 'billing', 'purpose']).default('day'),
  /** Only agent runs (`kind = run`) or only background work (reflection, consolidation, …). */
  work: z.enum(['runs', 'background']).optional(),
  /** Only API-key or only Claude-subscription calls. */
  billing: z.enum(['api', 'subscription']).optional(),
});
export const UsageRow = z.object({
  providerSearches: z.number().optional(),
  key: z.string(),
  label: z.string(),
  runs: z.number(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  cacheReadTokens: z.number(),
  cacheWriteTokens: z.number(),
  reasoningTokens: z.number(),
  costUsd: z.number(),
});
export type UsageRow = z.infer<typeof UsageRow>;
export const UsageSummary = z.object({
  rows: z.array(UsageRow),
  totals: UsageRow.omit({ key: true, label: true }),
  successRate: z.number(),
  latencyP50Ms: z.number().nullable(),
  latencyP95Ms: z.number().nullable(),
  priceMissing: z.boolean(),
});
export type UsageSummary = z.infer<typeof UsageSummary>;

// ---------- Budgets ----------
export const BudgetInput = z.object({
  scope: z.enum(['org', 'agent']),
  scopeId: Id.nullable(),
  period: z.enum(['day', 'month']),
  limitUsd: z.number().min(0).nullable(),
  limitTokens: z.number().int().min(0).nullable(),
  warnPct: z.number().int().min(1).max(100).default(80),
  hard: z.boolean().default(true),
});
export type BudgetInput = z.infer<typeof BudgetInput>;

/**
 * Route permission: a Permission, `null` = any authenticated org member (no specific permission),
 * `'public'` = no authentication.
 */
export type RoutePerm = Permission | null | 'public';
export interface RouteDef {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  perm: RoutePerm;
}

/** Route table: method, path and required permission. Used by the API (enforcement) and tests (RBAC matrix). */
export const ROUTES = {
  billingPlans: { method: 'GET', path: '/api/billing/plans', perm: 'public' },
  billingStatus: { method: 'GET', path: '/api/billing', perm: 'org:read' },
  billingCheckout: { method: 'POST', path: '/api/billing/checkout', perm: 'billing:manage' },
  billingPortal: { method: 'POST', path: '/api/billing/portal', perm: 'billing:manage' },
  billingWebhook: { method: 'POST', path: '/api/billing/webhook', perm: 'public' },
  /** Memory, knowledge / Obsidian and master chat (see knowledge.ts). */
  ...KNOWLEDGE_ROUTES,
  ...KB_ROUTES,
  ...CHANNEL_ROUTES,
  health: { method: 'GET', path: '/api/health', perm: 'public' },
  setupStatus: { method: 'GET', path: '/api/setup/status', perm: 'public' },
  me: { method: 'GET', path: '/api/me', perm: null },
  /** The caller's own login sessions (session only; never the session tokens themselves). */
  listMySessions: { method: 'GET', path: '/api/me/sessions', perm: null },
  /** Sign out one of the caller's own other sessions (session only, audited in the active company). */
  revokeMySession: { method: 'DELETE', path: '/api/me/sessions/:id', perm: null },
  companyFallbackPolicy: { method: 'GET', path: '/api/org/subscription-fallback', perm: 'org:read' },
  updateCompanyFallbackPolicy: {
    method: 'PATCH',
    path: '/api/org/subscription-fallback',
    perm: 'policy:update',
  },
  orgStatus: { method: 'GET', path: '/api/org/status', perm: 'org:read' },
  /** Companies (organizations) the caller belongs to, with a few live numbers each (docs/multi-company.md). */
  listCompanies: { method: 'GET', path: '/api/companies', perm: null },
  /** New company; platform admins only (+ 2FA), session only. The creator becomes its owner. */
  createCompany: { method: 'POST', path: '/api/companies', perm: null },
  /** Rename the active company / change its slug. */
  updateCompany: { method: 'PATCH', path: '/api/org', perm: 'org:update' },
  /** Delete the active company and everything in it (owner + 2FA, session only, typed confirmation). */
  deleteCompany: { method: 'DELETE', path: '/api/org', perm: 'org:delete' },
  /** First-run checklist counts (read-only, deterministic; the UI derives step completion from them). */
  onboardingStatus: { method: 'GET', path: '/api/onboarding', perm: 'org:read' },
  orgHalt: { method: 'POST', path: '/api/org/halt', perm: 'org:halt' },
  orgResume: { method: 'POST', path: '/api/org/resume', perm: 'org:halt' },
  listProviders: { method: 'GET', path: '/api/providers', perm: 'provider:read' },
  createProvider: { method: 'POST', path: '/api/providers', perm: 'provider:create' },
  updateProvider: { method: 'PATCH', path: '/api/providers/:id', perm: 'provider:update' },
  deleteProvider: { method: 'DELETE', path: '/api/providers/:id', perm: 'provider:delete' },
  testProvider: { method: 'POST', path: '/api/providers/:id/test', perm: 'provider:update' },
  syncModels: { method: 'POST', path: '/api/providers/:id/sync-models', perm: 'provider:update' },
  startCodexLogin: { method: 'POST', path: '/api/providers/:id/codex/login', perm: 'provider:update' },
  pollCodexLogin: {
    method: 'POST',
    path: '/api/providers/:id/codex/login/:loginId',
    perm: 'provider:update',
  },
  disconnectCodex: { method: 'POST', path: '/api/providers/:id/codex/disconnect', perm: 'provider:update' },
  getClaudeLogin: { method: 'GET', path: '/api/providers/:id/claude-login', perm: 'provider:read' },
  /** "Tell me when Codex on a ChatGPT plan is available" (hosted; waits for OpenAI's approval). */
  joinChatgptWaitlist: { method: 'POST', path: '/api/providers/chatgpt-waitlist', perm: 'provider:read' },
  startClaudeLogin: { method: 'POST', path: '/api/providers/:id/claude-login', perm: 'provider:update' },
  submitClaudeLoginCode: {
    method: 'POST',
    path: '/api/providers/:id/claude-login/code',
    perm: 'provider:update',
  },
  disconnectClaudeLogin: {
    method: 'DELETE',
    path: '/api/providers/:id/claude-login',
    perm: 'provider:update',
  },
  /** "Run this agent on my own Claude account" (or back to its creator's): only for yourself. */
  setAgentLoginOwner: { method: 'POST', path: '/api/agents/:id/claude-login-owner', perm: 'agent:update' },
  listModels: { method: 'GET', path: '/api/models', perm: 'provider:read' },
  updateModel: { method: 'PATCH', path: '/api/models/:id', perm: 'provider:update' },
  overridePrice: { method: 'PUT', path: '/api/models/:id/price', perm: 'provider:update' },
  listAgents: { method: 'GET', path: '/api/agents', perm: 'agent:read' },
  bulkAgentPermissions: { method: 'POST', path: '/api/agents/permissions/bulk', perm: 'agent:update' },
  getAgent: { method: 'GET', path: '/api/agents/:id', perm: 'agent:read' },
  previewAgentPermissions: {
    method: 'POST',
    path: '/api/agents/:id/permissions/preview',
    perm: 'policy:read',
  },
  createAgent: { method: 'POST', path: '/api/agents', perm: 'agent:create' },
  draftAgent: { method: 'POST', path: '/api/agents/draft', perm: 'agent:create' },
  improveInstructions: { method: 'POST', path: '/api/agents/instructions/improve', perm: 'agent:update' },
  updateAgent: { method: 'PATCH', path: '/api/agents/:id', perm: 'agent:update' },
  deleteAgent: { method: 'DELETE', path: '/api/agents/:id', perm: 'agent:delete' },
  listAgentVersions: { method: 'GET', path: '/api/agents/:id/versions', perm: 'agent:read' },
  createAgentVersion: { method: 'POST', path: '/api/agents/:id/versions', perm: 'agent:update' },
  listAgentWorkspaces: { method: 'GET', path: '/api/agents/:id/workspaces', perm: 'agent:read' },
  agentDelegationStats: { method: 'GET', path: '/api/agents/:id/delegation-stats', perm: 'agent:read' },
  agentWorkspaceAction: { method: 'POST', path: '/api/agents/:id/workspaces/actions', perm: 'agent:update' },
  activateAgentVersion: {
    method: 'POST',
    path: '/api/agents/:id/versions/:versionId/activate',
    perm: 'agent:update',
  },
  listConversations: { method: 'GET', path: '/api/conversations', perm: 'conversation:read' },
  createConversation: { method: 'POST', path: '/api/conversations', perm: 'conversation:create' },
  getMessages: { method: 'GET', path: '/api/conversations/:id/messages', perm: 'conversation:read' },
  setConversationRead: { method: 'POST', path: '/api/conversations/:id/read', perm: 'conversation:read' },
  getChatFallback: { method: 'GET', path: '/api/conversations/:id/fallback', perm: 'conversation:read' },
  sendMessage: { method: 'POST', path: '/api/conversations/:id/messages', perm: 'agent:run' },
  uploadAttachment: { method: 'POST', path: '/api/conversations/:id/attachments', perm: 'agent:run' },
  getAttachment: { method: 'GET', path: '/api/attachments/:id', perm: 'conversation:read' },
  artifactPreview: { method: 'GET', path: '/api/artifacts/preview', perm: 'run:read' },
  listRuns: { method: 'GET', path: '/api/runs', perm: 'run:read' },
  getRun: { method: 'GET', path: '/api/runs/:id', perm: 'run:read' },
  setRunAutoConsent: { method: 'POST', path: '/api/runs/:id/auto-consent', perm: 'policy:update' },
  /** What a run was given besides its prompt: memories, skills and earlier work in its task chain. */
  getRunContext: { method: 'GET', path: '/api/runs/:id/context', perm: 'run:read' },
  /** Compact deterministic status text of a run (no model call): status, step, cost, last tools, waiting on, last error. */
  getRunSummary: { method: 'GET', path: '/api/runs/:id/summary', perm: 'run:read' },
  runEvents: { method: 'GET', path: '/api/runs/:id/events', perm: 'run:read' },
  exportRunLogs: { method: 'GET', path: '/api/runs/:id/logs/export', perm: 'run:read' },
  runStream: { method: 'GET', path: '/api/runs/:id/stream', perm: 'run:read' },
  cancelRun: { method: 'POST', path: '/api/runs/:id/cancel', perm: 'run:cancel' },
  /** Resume a scheduled (usage limit) run now, from its saved progress. 409 when it is no longer waiting. */
  continueRun: { method: 'POST', path: '/api/runs/:id/continue', perm: 'run:resume' },
  /** Start a NEW run with the same agent and input as a finished one (the original stays untouched). */
  restartRun: { method: 'POST', path: '/api/runs/:id/restart', perm: 'agent:run' },
  listTasks: { method: 'GET', path: '/api/tasks', perm: 'task:read' },
  createTask: { method: 'POST', path: '/api/tasks', perm: 'task:create' },
  cancelTask: { method: 'POST', path: '/api/tasks/:id/cancel', perm: 'task:cancel' },
  listApprovals: { method: 'GET', path: '/api/approvals', perm: 'approval:read' },
  decideApproval: { method: 'POST', path: '/api/approvals/:id/decide', perm: 'approval:decide' },
  /** Hosts the egress proxy refused agents' sandboxes: pending ones need a human decision. */
  listEgressRequests: { method: 'GET', path: '/api/egress-requests', perm: 'approval:read' },
  /** Allow (agent:update + second factor for owner/admin, like request_network_access) or deny a host. */
  decideEgressRequest: { method: 'POST', path: '/api/egress-requests/:id/decide', perm: 'approval:decide' },
  /** Open and recently answered `ask_human` questions of all runs (answer them with `answerRun`). */
  listQuestions: { method: 'GET', path: '/api/questions', perm: 'run:read' },
  usage: { method: 'GET', path: '/api/usage', perm: 'usage:read' },
  listBudgets: { method: 'GET', path: '/api/budgets', perm: 'budget:read' },
  upsertBudget: { method: 'PUT', path: '/api/budgets', perm: 'budget:update' },
  listNotifications: { method: 'GET', path: '/api/notifications', perm: 'notification:read' },
  markNotificationRead: { method: 'POST', path: '/api/notifications/:id/read', perm: 'notification:update' },
  telegramLinkCode: {
    method: 'POST',
    path: '/api/notifications/telegram/link-code',
    perm: 'notification:update',
  },
  savePushSubscription: {
    method: 'POST',
    path: '/api/notifications/push-subscriptions',
    perm: 'notification:update',
  },
  deletePushSubscription: {
    method: 'DELETE',
    path: '/api/notifications/push-subscriptions',
    perm: 'notification:update',
  },
  vapidPublicKey: { method: 'GET', path: '/api/notifications/vapid-public-key', perm: 'notification:read' },
  answerRun: { method: 'POST', path: '/api/runs/:id/answer', perm: 'agent:run' },
  listTriggers: { method: 'GET', path: '/api/triggers', perm: 'trigger:read' },
  getTrigger: { method: 'GET', path: '/api/triggers/:id', perm: 'trigger:read' },
  createTrigger: { method: 'POST', path: '/api/triggers', perm: 'trigger:create' },
  updateTrigger: { method: 'PATCH', path: '/api/triggers/:id', perm: 'trigger:update' },
  deleteTrigger: { method: 'DELETE', path: '/api/triggers/:id', perm: 'trigger:delete' },
  fireTrigger: { method: 'POST', path: '/api/triggers/:id/fire', perm: 'trigger:update' },
  listTriggerFires: { method: 'GET', path: '/api/triggers/:id/fires', perm: 'trigger:read' },
  listMcpServers: { method: 'GET', path: '/api/mcp-servers', perm: 'mcp:read' },
  createMcpServer: { method: 'POST', path: '/api/mcp-servers', perm: 'mcp:create' },
  updateMcpServer: { method: 'PATCH', path: '/api/mcp-servers/:id', perm: 'mcp:update' },
  deleteMcpServer: { method: 'DELETE', path: '/api/mcp-servers/:id', perm: 'mcp:delete' },
  discoverMcpServer: { method: 'POST', path: '/api/mcp-servers/:id/discover', perm: 'mcp:update' },
  /** Repositories the server's credential reaches (presets with `repoApi`), via the vendor's REST API. */
  listMcpRepos: { method: 'POST', path: '/api/mcp-servers/:id/repos', perm: 'mcp:update' },
  /** Start the MCP OAuth sign-in for an `oauth` server: returns the provider's authorize URL. */
  startMcpOAuth: { method: 'POST', path: '/api/mcp-servers/:id/oauth/start', perm: 'mcp:update' },
  /**
   * OAuth redirect target (browser navigation from the provider). Needs the session of the user
   * who started the flow; re-checks `mcp:update` itself and redirects back into the app.
   */
  mcpOAuthCallback: { method: 'GET', path: '/api/mcp-oauth/callback', perm: null },
  listToolPolicies: { method: 'GET', path: '/api/tool-policies', perm: 'policy:read' },
  replaceToolPolicies: { method: 'PUT', path: '/api/tool-policies', perm: 'policy:update' },
  /** Server-to-server webhooks live under /api/hooks/ (exempt from the Origin check; authenticated by secret). */
  telegramWebhook: { method: 'POST', path: '/api/hooks/telegram', perm: 'public' },
  triggerWebhook: { method: 'POST', path: '/api/hooks/triggers/:id', perm: 'public' },
  events: { method: 'GET', path: '/api/events', perm: 'org:read' },
  runAnalysisSettings: { method: 'GET', path: '/api/run-analysis/settings', perm: 'analysis:read' },
  updateRunAnalysisSettings: {
    method: 'PATCH',
    path: '/api/run-analysis/settings',
    perm: 'analysis:configure',
  },
  listRunAnalysisReports: { method: 'GET', path: '/api/run-analysis/reports', perm: 'analysis:read' },
  getRunAnalysisReport: { method: 'GET', path: '/api/run-analysis/reports/:id', perm: 'analysis:read' },
  /** Generate (or refresh) today's report now, e.g. to test; still at most one report per org per day. */
  runRunAnalysis: { method: 'POST', path: '/api/run-analysis/run', perm: 'analysis:run' },
  auditLog: { method: 'GET', path: '/api/audit', perm: 'audit:read' },
  auditFacets: { method: 'GET', path: '/api/audit/facets', perm: 'audit:read' },
  listMembers: { method: 'GET', path: '/api/members', perm: 'member:read' },
  listNotifyChannels: { method: 'GET', path: '/api/notifications/channels', perm: 'notification:read' },
  createNotifyChannel: { method: 'POST', path: '/api/notifications/channels', perm: 'notification:update' },
  updateNotifyChannel: {
    method: 'PATCH',
    path: '/api/notifications/channels/:id',
    perm: 'notification:update',
  },
  deleteNotifyChannel: {
    method: 'DELETE',
    path: '/api/notifications/channels/:id',
    perm: 'notification:update',
  },
  sendChannelCode: {
    method: 'POST',
    path: '/api/notifications/channels/:id/send-code',
    perm: 'notification:update',
  },
  verifyNotifyChannel: {
    method: 'POST',
    path: '/api/notifications/channels/:id/verify',
    perm: 'notification:update',
  },
  testNotifyChannel: {
    method: 'POST',
    path: '/api/notifications/channels/:id/test',
    perm: 'notification:update',
  },
  listNotifyRules: { method: 'GET', path: '/api/notifications/rules', perm: 'notification:read' },
  replaceNotifyRules: { method: 'PUT', path: '/api/notifications/rules', perm: 'notification:update' },
  listDeliveries: { method: 'GET', path: '/api/notifications/deliveries', perm: 'notification:read' },
  telegramStatus: { method: 'GET', path: '/api/notifications/telegram', perm: 'notification:read' },
  /**
   * Personal access tokens (own tokens only; never usable with a token themselves). A token never
   * exceeds its creator's live role, and privileged (admin-level) actions are refused for tokens.
   */
  listApiTokens: { method: 'GET', path: '/api/tokens', perm: 'token:read' },
  createApiToken: { method: 'POST', path: '/api/tokens', perm: 'token:manage' },
  revokeApiToken: { method: 'DELETE', path: '/api/tokens/:id', perm: 'token:manage' },
  /** Recording mode (the Chrome extension posts events with a `recorder`-scoped token). */
  listRecordings: { method: 'GET', path: '/api/recordings', perm: 'recording:read' },
  createRecording: { method: 'POST', path: '/api/recordings', perm: 'recording:create' },
  getRecording: { method: 'GET', path: '/api/recordings/:id', perm: 'recording:read' },
  listRecordingEvents: { method: 'GET', path: '/api/recordings/:id/events', perm: 'recording:read' },
  ingestRecordingEvents: { method: 'POST', path: '/api/recordings/:id/events', perm: 'recording:create' },
  /** Stop recording and synthesize a flow (worker job). */
  stopRecording: { method: 'POST', path: '/api/recordings/:id/stop', perm: 'recording:create' },
  /** Synthesize again (e.g. after connecting a missing integration). */
  synthesizeRecording: { method: 'POST', path: '/api/recordings/:id/synthesize', perm: 'recording:create' },
  deleteRecording: { method: 'DELETE', path: '/api/recordings/:id', perm: 'recording:delete' },
  listFlows: { method: 'GET', path: '/api/flows', perm: 'flow:read' },
  getFlow: { method: 'GET', path: '/api/flows/:id', perm: 'flow:read' },
  createFlow: { method: 'POST', path: '/api/flows', perm: 'flow:create' },
  updateFlow: { method: 'PATCH', path: '/api/flows/:id', perm: 'flow:update' },
  deleteFlow: { method: 'DELETE', path: '/api/flows/:id', perm: 'flow:delete' },
  listFlowVersions: { method: 'GET', path: '/api/flows/:id/versions', perm: 'flow:read' },
  createFlowVersion: { method: 'POST', path: '/api/flows/:id/versions', perm: 'flow:update' },
  /** Ranked agents to run this flow (deterministic scoring, no model call). */
  suggestFlowAgents: { method: 'GET', path: '/api/flows/:id/suggestions', perm: 'flow:read' },
  /** Bind the flow to an agent: a new agent version (+ the MCP bindings the flow needs). */
  attachFlow: { method: 'POST', path: '/api/flows/:id/attach', perm: 'agent:update' },
  runFlow: { method: 'POST', path: '/api/flows/:id/run', perm: 'flow:run' },
  /** Skills (open Agent Skills format). */
  listSkills: { method: 'GET', path: '/api/skills', perm: 'skill:read' },
  getSkill: { method: 'GET', path: '/api/skills/:id', perm: 'skill:read' },
  createSkill: { method: 'POST', path: '/api/skills', perm: 'skill:create' },
  importSkill: { method: 'POST', path: '/api/skills/import', perm: 'skill:create' },
  exportSkill: { method: 'GET', path: '/api/skills/:id/export', perm: 'skill:read' },
  listSkillVersions: { method: 'GET', path: '/api/skills/:id/versions', perm: 'skill:read' },
  createSkillVersion: { method: 'POST', path: '/api/skills/:id/versions', perm: 'skill:update' },
  /** Make a proposed version live (human, session-only). */
  activateSkill: { method: 'POST', path: '/api/skills/:id/activate', perm: 'skill:activate' },
  rejectSkill: { method: 'POST', path: '/api/skills/:id/reject', perm: 'skill:activate' },
  /** Agent-drafted skills (and proposed edits) waiting for a human, with the evidence behind them. */
  listSkillSuggestions: { method: 'GET', path: '/api/skills/suggestions', perm: 'skill:read' },
  /** Per skill: catalog exposures, loads, load rate and catalog token cost (last 30 days). */
  listSkillUsage: { method: 'GET', path: '/api/skills/usage', perm: 'skill:read' },
  /** Add a suggestion, optionally after editing it (human, session-only). */
  acceptSkill: { method: 'POST', path: '/api/skills/:id/accept', perm: 'skill:activate' },
  /** Bind a live skill to an agent: a new agent version. */
  attachSkill: { method: 'POST', path: '/api/skills/:id/attach', perm: 'agent:update' },
  /** Archives (soft delete) the skill. */
  deleteSkill: { method: 'DELETE', path: '/api/skills/:id', perm: 'skill:delete' },
  /** MCP (Streamable HTTP) for the user's own Claude Code / MCP client; authenticated by an `afp_` token. */
  mcpEndpoint: { method: 'POST', path: '/api/mcp', perm: 'public' },
  /** Stateless MCP server: 405 for the optional SSE stream / session delete. */
  mcpEndpointGet: { method: 'GET', path: '/api/mcp', perm: 'public' },
  /** Admin action: register `${APP_ORIGIN}/api/hooks/telegram` (+ secret) with the Bot API. */
  telegramRegisterWebhook: {
    method: 'POST',
    path: '/api/notifications/telegram/webhook',
    perm: 'secret:update',
  },
  // ---- platform admin (docs/platform-admin.md): `perm: null` + the platform guard in route(); 404 for anyone else
  platformOverview: { method: 'GET', path: '/api/platform/overview', perm: null },
  platformStats: { method: 'GET', path: '/api/platform/stats', perm: null },
  platformLive: { method: 'GET', path: '/api/platform/live', perm: null },
  platformRuns: { method: 'GET', path: '/api/platform/runs', perm: null },
  platformRun: { method: 'GET', path: '/api/platform/runs/:id', perm: null },
  platformCancelRun: { method: 'POST', path: '/api/platform/runs/:id/cancel', perm: null },
  platformAgents: { method: 'GET', path: '/api/platform/agents', perm: null },
  platformAccounts: { method: 'GET', path: '/api/platform/billing-accounts', perm: null },
  platformSystem: { method: 'GET', path: '/api/platform/system', perm: null },
  platformUsers: { method: 'GET', path: '/api/platform/users', perm: null },
  platformUser: { method: 'GET', path: '/api/platform/users/:id', perm: null },
  platformCreateUser: { method: 'POST', path: '/api/platform/users', perm: null },
  platformDeleteUser: { method: 'POST', path: '/api/platform/users/:id/delete', perm: null },
  platformBanUser: { method: 'POST', path: '/api/platform/users/:id/ban', perm: null },
  platformUnbanUser: { method: 'POST', path: '/api/platform/users/:id/unban', perm: null },
  platformRevokeSessions: { method: 'POST', path: '/api/platform/users/:id/revoke-sessions', perm: null },
  platformReset2fa: { method: 'POST', path: '/api/platform/users/:id/reset-2fa', perm: null },
  platformImpersonate: { method: 'POST', path: '/api/platform/users/:id/impersonate', perm: null },
  platformUpdateUser: { method: 'POST', path: '/api/platform/users/:id/profile', perm: null },
  platformSetPlatformAdmin: { method: 'POST', path: '/api/platform/users/:id/platform-admin', perm: null },
  platformRevokeSession: {
    method: 'POST',
    path: '/api/platform/users/:id/sessions/:sessionId/revoke',
    perm: null,
  },
  platformPasswordReset: { method: 'POST', path: '/api/platform/users/:id/password-reset', perm: null },
  platformSetMemberRole: {
    method: 'POST',
    path: '/api/platform/companies/:id/members/:userId/role',
    perm: null,
  },
  platformRemoveMember: {
    method: 'POST',
    path: '/api/platform/companies/:id/members/:userId/remove',
    perm: null,
  },
  platformRenameCompany: { method: 'POST', path: '/api/platform/companies/:id/rename', perm: null },
  platformHaltCompany: { method: 'POST', path: '/api/platform/companies/:id/halt', perm: null },
  platformResumeCompany: { method: 'POST', path: '/api/platform/companies/:id/resume', perm: null },
  platformDeleteCompany: { method: 'POST', path: '/api/platform/companies/:id/delete', perm: null },
  platformCreateCompany: { method: 'POST', path: '/api/platform/companies', perm: null },
  platformAddMember: { method: 'POST', path: '/api/platform/companies/:id/members', perm: null },
  platformCompanies: { method: 'GET', path: '/api/platform/companies', perm: null },
  platformCompany: { method: 'GET', path: '/api/platform/companies/:id', perm: null },
  platformAccount: { method: 'GET', path: '/api/platform/billing-accounts/:id', perm: null },
  platformSetPlan: { method: 'POST', path: '/api/platform/billing-accounts/:id/plan', perm: null },
  platformSetOwner: { method: 'POST', path: '/api/platform/billing-accounts/:id/owner', perm: null },
  platformExtendTrial: {
    method: 'POST',
    path: '/api/platform/billing-accounts/:id/extend-trial',
    perm: null,
  },
  platformCancelSubscription: {
    method: 'POST',
    path: '/api/platform/billing-accounts/:id/cancel',
    perm: null,
  },
  platformCredit: { method: 'POST', path: '/api/platform/billing-accounts/:id/credit', perm: null },
  platformPayments: { method: 'GET', path: '/api/platform/payments', perm: null },
  platformRefund: { method: 'POST', path: '/api/platform/invoices/:id/refund', perm: null },
  platformInvoiceAction: { method: 'POST', path: '/api/platform/invoices/:id/action', perm: null },
  platformAudit: { method: 'GET', path: '/api/platform/audit', perm: null },
  platformSetKor: { method: 'POST', path: '/api/platform/settings/kor', perm: null },
  platformAiConfig: { method: 'GET', path: '/api/platform/settings/ai', perm: null },
  platformSetAiConfig: { method: 'POST', path: '/api/platform/settings/ai', perm: null },
  platformAnnouncement: { method: 'GET', path: '/api/platform/settings/announcement', perm: null },
  platformSetAnnouncement: { method: 'POST', path: '/api/platform/settings/announcement', perm: null },
} as const satisfies Record<string, RouteDef>;
export type RouteKey = keyof typeof ROUTES;

/** The only routes a `recorder`-scoped token (the Chrome extension) may call. */
export const RECORDER_TOKEN_ROUTES: ReadonlySet<RouteKey> = new Set<RouteKey>([
  'me',
  'listRecordings',
  'createRecording',
  'getRecording',
  'ingestRecordingEvents',
  'stopRecording',
]);

// ---------- Additional DTOs (apps/api ↔ apps/web) ----------
export const CursorQuery = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const HealthDto = z.object({ ok: z.literal(true), version: z.string() });
export const SetupStatusDto = z.object({ needsSetup: z.boolean() });
export type SetupStatusDto = z.infer<typeof SetupStatusDto>;

export const MeDto = z.object({
  billing: BillingDto.nullable().optional(),
  user: z.object({
    id: Id,
    name: z.string(),
    email: z.string(),
    image: z.string().nullable(),
    twoFactorEnabled: z.boolean(),
    hasPasskey: z.boolean(),
  }),
  organization: z.object({ id: Id, name: z.string(), slug: z.string() }).nullable(),
  role: z.enum(ROLES).nullable(),
  permissions: z.array(z.string()),
  /** True when the role requires a second factor and none is enrolled (privileged mutations return 403 `2fa_required`). */
  needs2fa: z.boolean(),
  /** Server capabilities the UI adapts to. */
  features: z
    .object({
      claudeSubscription: z.boolean(),
      codexSubscription: z.boolean().optional(),
      /** Each person's own Claude Code login inside their agents' sandbox (Claude mode `sandbox_login`). */
      claudeLogin: z.boolean().optional(),
      /** AF_DEPLOYMENT=hosted: no stored subscription credentials (docs/plans/2026-10-subscription-compliance-v2.md). */
      hosted: z.boolean().optional(),
    })
    .optional(),
  /** Platform admin (Better Auth admin role): may create companies. */
  platformAdmin: z.boolean().optional(),
  /** Set while a platform admin is signed in as this user for support: the session is read-only. */
  impersonation: z.object({ expiresAt: z.string() }).nullable().optional(),
  /** Platform-wide notice from the admin app (maintenance, incidents); dismissible per id. */
  announcement: Announcement.nullable().optional(),
});
export type MeDto = Omit<z.infer<typeof MeDto>, 'permissions'> & { permissions: Permission[] };

export const OrgStatusDto = z.object({
  orgId: Id,
  name: z.string(),
  agentsHalted: z.boolean(),
  haltedAt: z.string().nullable(),
  haltedBy: z.string().nullable(),
  activeRuns: z.number(),
  /** Tool approvals waiting for a decision plus open `ask_human` questions and egress requests waiting for a person. */
  pendingApprovals: z.number(),
  costTodayUsd: z.number(),
});
export type OrgStatusDto = z.infer<typeof OrgStatusDto>;

/** One company the caller belongs to (`listCompanies`). */
export const CompanyDto = z.object({
  id: Id,
  name: z.string(),
  slug: z.string(),
  role: z.enum(ROLES),
  agentsHalted: z.boolean(),
  activeRuns: z.number(),
  pendingApprovals: z.number(),
  costTodayUsd: z.number(),
  createdAt: z.string(),
});
export type CompanyDto = z.infer<typeof CompanyDto>;
export const CompanyInput = z.object({
  name: z.string().trim().min(1).max(80),
  /** Derived from the name when omitted. */
  slug: Slug.optional(),
});
export type CompanyInput = z.infer<typeof CompanyInput>;
export const CompanyPatch = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  slug: Slug.optional(),
});
export type CompanyPatch = z.infer<typeof CompanyPatch>;
/** Typed confirmation: the company's slug. */
export const CompanyDeleteInput = z.object({ confirm: z.string().min(1).max(64) });
export type CompanyDeleteInput = z.infer<typeof CompanyDeleteInput>;
/** Request header that pins a request to one company (per browser tab); membership is checked. */
export const ORG_HEADER = 'x-af-org';
/** Same pin as a query parameter, honoured on GET only (`<img src>` / downloads cannot send headers). */
export const ORG_QUERY = 'af_org';

/**
 * Counts behind the guided first-run flow. `budgets` = org-level budgets; `channels` = the caller's
 * enabled notification channels + push subscriptions.
 */
export const OnboardingStatusDto = z.object({
  providers: z.number().int(),
  enabledModels: z.number().int(),
  agents: z.number().int(),
  budgets: z.number().int(),
  channels: z.number().int(),
  conversations: z.number().int(),
});
export type OnboardingStatusDto = z.infer<typeof OnboardingStatusDto>;
export const HaltInput = z.object({ reason: z.string().max(500).optional() }).default({});
export const HaltResult = z.object({ agentsHalted: z.boolean(), cancelledRuns: z.number() });

export const ProviderPatch = z.object({
  /** Guarded disable from the move dialog; explicit disable-without-moving omits this. */
  requireNoAgents: z.boolean().optional(),
  name: z.string().min(1).max(80).optional(),
  baseUrl: z.string().url().max(500).nullable().optional(),
  /** Write-only. `null` removes the stored key. */
  apiKey: z.string().min(1).max(500).nullable().optional(),
  enabled: z.boolean().optional(),
  authMode: ProviderAuthMode.optional(),
  /** Write-only. `null` removes the stored subscription token. */
  subscriptionToken: SubscriptionToken.nullable().optional(),
});
export type ProviderPatch = z.infer<typeof ProviderPatch>;
export const ProviderTestResult = z.object({
  ok: z.boolean(),
  latencyMs: z.number(),
  modelCount: z.number().nullable(),
  error: z.string().nullable(),
});
export type ProviderTestResult = z.infer<typeof ProviderTestResult>;
export const SyncModelsResult = z.object({ added: z.number(), updated: z.number(), total: z.number() });

export const ModelsQuery = z.object({ providerId: Id.optional() });
export const ModelPatch = z.object({
  enabled: z.boolean().optional(),
  displayName: z.string().min(1).max(120).optional(),
});

export const ActivateVersionResult = z.object({ currentVersionId: Id });

export const ConversationInput = z.object({ agentId: Id, title: z.string().min(1).max(200).optional() });
/** Mark a chat read (opened) or unread (by hand, from the chat list). */
export const ConversationReadInput = z.object({ read: z.boolean() });
export const ConversationsQuery = z.object({
  agentId: Id.optional(),
  kind: z.enum(['agent', 'master']).default('agent'),
});

/**
 * Messages of a conversation, oldest first. Without `limit` all of them (compat); with `limit` the NEWEST
 * `limit`, and `before` (a message id) pages further back. `nextCursor` = `before` for the next older page.
 */
export const MessagesQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).optional(),
  before: z.string().uuid().optional(),
});

export const RunsQuery = CursorQuery.extend({
  agentId: Id.optional(),
  taskId: Id.optional(),
  conversationId: Id.optional(),
  /** Every run of one delegation tree (the root run itself excluded). */
  rootRunId: Id.optional(),
  status: z.string().max(40).optional(),
});
export const RunEventDto = z.object({
  seq: z.number(),
  type: z.string(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});
export type RunEventDto = z.infer<typeof RunEventDto>;
export const RunEventsQuery = z.object({
  after: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(1000).default(500),
  /** Only events of this type (e.g. `model-fallback`, to show a finished run's hand-offs without its whole log). */
  type: z.string().min(1).max(40).optional(),
});

export const TaskDto = z.object({
  id: Id,
  title: z.string(),
  input: z.string(),
  assigneeAgentId: Id,
  status: z.string(),
  priority: z.number(),
  dependsOn: z.array(Id),
  parentTaskId: Id.nullable(),
  chainDepth: z.number(),
  /** Most recent run for this task (null while blocked on dependencies). */
  runId: Id.nullable(),
  result: z.looseObject({ summary: z.string() }).nullable(),
  error: z.string().nullable(),
  dueAt: z.string().nullable(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
});
export type TaskDto = z.infer<typeof TaskDto>;
export const TasksQuery = CursorQuery.extend({
  status: z.string().max(40).optional(),
  assigneeAgentId: Id.optional(),
});

export const ApprovalsQuery = z.object({
  status: z.enum(['pending', 'approved', 'denied', 'expired']).optional(),
});

export const BudgetDto = BudgetInput.extend({
  id: Id,
  spentUsd: z.number(),
  reservedUsd: z.number(),
  spentTokens: z.number(),
  periodStart: z.string(),
});
export type BudgetDto = z.infer<typeof BudgetDto>;

export const NotificationDto = z.object({
  id: Id,
  eventType: z.string(),
  title: z.string(),
  body: z.string(),
  link: z.string().nullable(),
  readAt: z.string().nullable(),
  createdAt: z.string(),
});
export type NotificationDto = z.infer<typeof NotificationDto>;
export const NotificationsQuery = CursorQuery.extend({ unread: z.enum(['true', 'false']).optional() });

export const AuditActorDto = z.object({
  type: z.string(),
  id: z.string().nullable(),
  /** User name, agent name, trigger name or "System". */
  name: z.string(),
  email: z.string().nullable(),
});
export type AuditActorDto = z.infer<typeof AuditActorDto>;

export const AuditEntryDto = z.object({
  id: z.number(),
  /** Resolved actor (ids stay available in actorType/actorId). */
  actor: AuditActorDto.optional(),
  /** Resolved target display name (null when unknown/deleted). */
  targetName: z.string().nullable().optional(),
  /** Readable action label, e.g. "Changed a member role". */
  label: z.string().optional(),
  actorType: z.string(),
  actorId: z.string().nullable(),
  action: z.string(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  ip: z.string().nullable(),
  before: z.unknown(),
  after: z.unknown(),
  hash: z.string(),
  createdAt: z.string(),
});
export type AuditEntryDto = z.infer<typeof AuditEntryDto>;

export const AuditListQuery = CursorQuery.extend({
  cursor: z.coerce.number().int().positive().optional(),
  actorId: z.string().max(64).optional(),
  actorType: z.enum(['user', 'agent', 'trigger', 'system']).optional(),
  /** Exact action, or a prefix ending in "." / "*" (e.g. "auth." or "member.*"). */
  action: z.string().max(80).optional(),
  /** ISO date or datetime, inclusive. */
  from: z.string().max(40).optional(),
  /** ISO date (whole day inclusive) or datetime. */
  to: z.string().max(40).optional(),
});
export const AuditFacetsDto = z.object({
  actors: z.array(AuditActorDto),
  actions: z.array(z.object({ action: z.string(), label: z.string() })),
});
export type AuditFacetsDto = z.infer<typeof AuditFacetsDto>;

/** Payload of `GET /api/events` (org-level SSE, event name `org`). */
export const OrgEventDto = z.object({ kind: z.string(), at: z.string() });

// ---------- Personal access tokens (MCP) ----------
export const API_TOKEN_PREFIX = 'afp_';
/** `recorder`: recording ingest only (RECORDER_TOKEN_ROUTES), for the Chrome extension. */
export const ApiTokenScope = z.enum(['read', 'write', 'recorder']);
export type ApiTokenScope = z.infer<typeof ApiTokenScope>;

export const ApiTokenInput = z.object({
  name: z.string().trim().min(1).max(80),
  scope: ApiTokenScope.default('write'),
  /** Days until expiry; null = never. */
  expiresInDays: z.number().int().min(1).max(365).nullable().default(90),
});
export type ApiTokenInput = z.infer<typeof ApiTokenInput>;

export const ApiTokenDto = z.object({
  id: Id,
  name: z.string(),
  prefix: z.string(),
  scope: ApiTokenScope,
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
});
export type ApiTokenDto = z.infer<typeof ApiTokenDto>;

/** One of the caller's login sessions (Settings → Security). Never carries the session token. */
export const MySessionDto = z.object({
  id: z.string(),
  userAgent: z.string().nullable(),
  ipAddress: z.string().nullable(),
  createdAt: z.string(),
  lastActiveAt: z.string(),
  expiresAt: z.string(),
  current: z.boolean(),
});
export type MySessionDto = z.infer<typeof MySessionDto>;

/** Returned once on creation; the plain token is never retrievable again. */
export const ApiTokenCreatedDto = ApiTokenDto.extend({ token: z.string(), mcpUrl: z.string() });
export type ApiTokenCreatedDto = z.infer<typeof ApiTokenCreatedDto>;
